import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";

import SmoothScroll from "@/components/SmoothScroll";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

import Home from "@/pages/Home";
import SectionPage from "@/pages/SectionPage";
import EssayDetail from "@/pages/EssayDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";
import { AuthProvider } from "@/context/AuthContext";

const SECTION_SLUGS = [
    "overachieving-for-me",
    "champagne-and-bagel-bites",
    "life-as-an-elective",
];

function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        // If there's a hash, let the target page's own hash handler scroll to it.
        if (hash) return;
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname, hash]);
    return null;
}

function AnimatedRoutes() {
    const location = useLocation();
    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={location.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
                <Routes location={location}>
                    <Route path="/" element={<Home />} />
                    {SECTION_SLUGS.map((s) => (
                        <Route key={s} path={`/${s}`} element={<SectionPage slug={s} />} />
                    ))}
                    <Route path="/essay/:slug" element={<EssayDetail />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<Admin />} />
                </Routes>
            </motion.div>
        </AnimatePresence>
    );
}

function Layout() {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith("/admin");
    return (
        <>
            {!isAdmin && <Nav />}
            <ScrollToTop />
            {isAdmin && (
                <div className="fixed top-0 left-0 right-0 z-50 border-b hairline bg-[rgb(var(--paper))]/90 backdrop-blur">
                    <div className="mx-auto max-w-[1280px] px-6 md:px-10 py-4">
                        <a href="/" className="wordmark text-base">
                            Overachieving <em>for me</em>
                            <span className="ml-3 overline">/ admin</span>
                        </a>
                    </div>
                </div>
            )}
            <main>
                <AnimatedRoutes />
            </main>
            {!isAdmin && <Footer />}
            <Toaster
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "rgb(18,59,74)",
                        color: "rgb(246,242,234)",
                        border: "1px solid rgb(184,148,74)",
                        borderRadius: 0,
                        fontFamily: "Karla, sans-serif",
                        fontSize: 14,
                    },
                }}
            />
        </>
    );
}

// Use SectionPage with params derived from path

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <SmoothScroll>
                    <Layout />
                </SmoothScroll>
            </AuthProvider>
        </BrowserRouter>
    );
}
